import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  LogOut,
  Search,
  RefreshCw,
  Phone,
  Mail,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Inbox,
  CheckCircle2,
  Hourglass,
  Award,
  Archive,
  Download,
  AlertCircle,
  Flame,
  BookOpen,
  IndianRupee,
  ClipboardCheck,
  ShieldOff,
  Users,
  Trash2,
  Undo2,
  XCircle,
  ShieldCheck,
  History,
  Layers,
  Activity,
  BarChart3,
  Plus,
  UserPlus,
  Calendar,
  ChevronDown,
  Send,
  X,
} from "lucide-react";
import { adminApi, tokenStorage, API, AdminLeadOps, CRM, currentUser, AdminStaff, SavedViews } from "@/lib/api";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import BrandBanner from "@/components/BrandBanner";
import DashboardBrandStrip from "@/components/DashboardBrandStrip";

const STATUS_META = {
  new: { label: "New", color: "bg-blue-400/15 text-blue-200 border-blue-400/30", icon: Inbox },
  contacted: {
    label: "Contacted",
    color: "bg-yellow-300/15 text-yellow-200 border-yellow-300/30",
    icon: Hourglass,
  },
  qualified: {
    label: "Qualified",
    color: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
    icon: Award,
  },
  closed: {
    label: "Closed",
    color: "bg-zinc-400/10 text-zinc-300 border-zinc-400/25",
    icon: Archive,
  },
  pending: { label: "Pending", color: "bg-amber-400/10 text-amber-200 border-amber-400/30", icon: Hourglass },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-200 border-emerald-400/30", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-500/10 text-red-200 border-red-400/30", icon: XCircle },
  disbursed: { label: "Disbursed", color: "bg-[#D4AF37]/15 text-[#F0D788] border-[#D4AF37]/40", icon: IndianRupee },
};

const ALL_STATUSES = ["new", "contacted", "qualified", "closed", "pending", "approved", "rejected", "disbursed"];
const PAGE_SIZE = 25;

const VERIF_META = {
  pending:  { label: "Pending",  color: "bg-amber-500/10 text-amber-200 border-amber-400/30", icon: Hourglass },
  verified: { label: "Verified", color: "bg-emerald-500/10 text-emerald-200 border-emerald-400/30", icon: ShieldCheck },
  rejected: { label: "Rejected", color: "bg-red-500/10 text-red-200 border-red-400/30", icon: XCircle },
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const me = currentUser();
  const role = me?.role || "operator";
  const isAdmin = role === "admin";
  const canVerify = role === "admin" || role === "manager";
  const canWrite = ["admin", "manager", "receptionist", "operator"].includes(role);

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, by_status: [], by_interest: [] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [trashMode, setTrashMode] = useState(false);
  const [verifModal, setVerifModal] = useState(null); // {lead, status}
  const [deleteModal, setDeleteModal] = useState(null); // {lead, kind:'soft'|'permanent'}
  const [detailModal, setDetailModal] = useState(null); // {lead}
  const [selectedIds, setSelectedIds] = useState([]); // for bulk
  const [bulkModal, setBulkModal] = useState(null); // {action}
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveViewInput, setShowSaveViewInput] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const hasToken = Boolean(tokenStorage.get());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      if (trashMode) params.trash = true;
      const [leadsResp, statsResp, viewsResp] = await Promise.all([
        adminApi.get("/leads", { params }),
        adminApi.get("/leads/stats"),
        SavedViews.list().catch(() => ({ items: [] })),
      ]);
      setLeads(leadsResp.data.items);
      setTotal(leadsResp.data.total);
      setStats(statsResp.data);
      setSavedViews(viewsResp.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin", { replace: true });
        return;
      }
      setError(err?.response?.data?.detail || "Could not load leads");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, trashMode, navigate]);

  async function saveCurrentView() {
    const name = saveViewName.trim();
    if (!name) return;
    await SavedViews.create(name, { status: statusFilter, search, trash: trashMode });
    setSaveViewName("");
    setShowSaveViewInput(false);
    load();
  }

  function applyView(v) {
    if (v.filters?.status) setStatusFilter(v.filters.status);
    if (v.filters?.search !== undefined) setSearch(v.filters.search);
    if (v.filters?.trash !== undefined) setTrashMode(v.filters.trash);
    setPage(0);
  }

  async function deleteView(id, e) {
    e.stopPropagation();
    await SavedViews.remove(id);
    load();
  }

  useEffect(() => {
    if (hasToken) load();
  }, [load, hasToken]);

  const filteredLeads = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.toLowerCase();
    return leads.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.phone.includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        l.interest.toLowerCase().includes(q)
    );
  }, [leads, search]);

  if (!hasToken) {
    return <Navigate to="/admin" replace />;
  }

  const updateStatus = async (lead, status) => {
    if (lead.status === status) return;
    try {
      const { data } = await adminApi.patch(`/leads/${lead.id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: data.status } : l)));
      // refresh stats inline
      const statsResp = await adminApi.get("/leads/stats");
      setStats(statsResp.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update");
    }
  };

  const logout = async () => {
    try {
      await adminApi.post("/auth/logout");
    } catch (_) {}
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const exportCsv = async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const resp = await adminApi.get("/leads/export.csv", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "");
      a.href = url;
      a.download = `sahyog-leads-${statusFilter === "all" ? "all" : statusFilter}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err?.response?.data?.detail || "CSV download failed");
    }
  };


  const statusCount = (s) =>
    stats.by_status.find((it) => it._id === s)?.count || 0;

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div
      data-testid="admin-leads-page"
      className="min-h-screen bg-[#04102B] text-white"
    >
      {/* Top bar */}      <header className="sticky top-0 z-30 bg-[#04102B]/90 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[140px] flex items-center justify-between gap-4">
          <button
            data-testid="admin-back-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-4 text-left"
          >
            <div className="leading-tight">
              <div className="font-display text-sm md:text-lg text-white uppercase tracking-tight font-semibold whitespace-nowrap">
                SAHYOG FINANCE
                <br />
                <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
              </div>
              <div className="font-display text-[#D4AF37] text-[10px] md:text-xs tracking-[0.18em] uppercase mt-1">
                Asset Management Pvt. Ltd. · Admin Console
              </div>
            </div>
          </button>

          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              disabled={loading}
              className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 text-base font-semibold"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="text-[#0A1F44] inline-flex items-center gap-2 px-5 py-3 rounded-full text-base font-semibold btn-gold"
            >
              <Download size={20} /> Export CSV
            </button>
            <a
              href="/admin/change-password"
              data-testid="admin-change-pw-link"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-base font-semibold"
            >
              Change password
            </a>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-sm border border-white/10"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
        <div className="mb-8">
          <DashboardBrandStrip />
        </div>
        {/* Admin nav tabs */}
        <section className="mb-8">
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid="nav-tab-leads"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] border border-[#D4AF37] text-sm font-semibold"
            >
              <Inbox size={14} /> Leads
            </span>
            <Link
              data-testid="nav-tab-applications"
              to="/admin/applications"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <Award size={14} /> Applications
            </Link>
            <Link
              data-testid="nav-tab-grievances"
              to="/admin/grievances"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <AlertCircle size={14} /> Grievances
            </Link>
            <Link
              data-testid="nav-tab-offers"
              to="/admin/offers"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <Flame size={14} /> Hot Offers
            </Link>
            <Link
              data-testid="nav-tab-blogs"
              to="/admin/blogs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <BookOpen size={14} /> Blogs
            </Link>
            <Link
              data-testid="nav-tab-onboarding-log"
              to="/admin/onboarding-log"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <Mail size={14} /> Onboarding Log
            </Link>
            <Link
              data-testid="nav-tab-invitations"
              to="/admin/invitations"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <Mail size={14} /> Invitations
            </Link>
            <Link
              data-testid="nav-tab-investors"
              to="/admin/investors"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <Award size={14} /> Investors
            </Link>
            <Link
              data-testid="nav-tab-manual-payments"
              to="/admin/manual-payments"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <IndianRupee size={14} /> Manual Payments
            </Link>
            <Link
              data-testid="nav-tab-eligibility-checks"
              to="/admin/eligibility-checks"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <ClipboardCheck size={14} /> Eligibility Checks
            </Link>
            <Link
              data-testid="nav-tab-partners"
              to="/admin/partners"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <ShieldOff size={14} /> Partners &amp; Termination
            </Link>
            <Link
              data-testid="nav-tab-registrations"
              to="/admin/registrations"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"
            >
              <Download size={14} /> Registrations · PDF
            </Link>
            <Link
              data-testid="nav-tab-security"
              to="/admin/security"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#F0D788] hover:bg-[#D4AF37]/20 text-sm"
            >
              <ShieldOff size={14} /> Security · 2FA
            </Link>
            {(isAdmin || role === "manager") && (
              <>
                <Link
                  data-testid="nav-tab-dashboard"
                  to="/admin/dashboard"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#F0D788] hover:bg-[#D4AF37]/20 text-sm"
                >
                  <BarChart3 size={14} /> CRM Dashboard
                </Link>
                <Link
                  data-testid="nav-tab-pipeline"
                  to="/admin/pipeline"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#F0D788] hover:bg-[#D4AF37]/20 text-sm"
                >
                  <Layers size={14} /> Kanban
                </Link>
              </>
            )}
            {(isAdmin || role === "manager" || role === "accountant" || role === "receptionist") && (
              <Link
                data-testid="nav-tab-customer360"
                to="/admin/customer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#F0D788] hover:bg-[#D4AF37]/20 text-sm"
              >
                <UserPlus size={14} /> Customer 360°
              </Link>
            )}
            {isAdmin && (
              <Link
                data-testid="nav-tab-staff"
                to="/admin/staff"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#F0D788] hover:bg-[#D4AF37]/20 text-sm"
              >
                <Users size={14} /> Team & Access
              </Link>
            )}
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard testId="stat-total" label="Total leads" value={stats.total} />
          {ALL_STATUSES.map((s) => {
            const M = STATUS_META[s];
            return (
              <StatCard
                key={s}
                testId={`stat-${s}`}
                label={M.label}
                value={statusCount(s)}
                accent={s === "qualified" ? "text-emerald-300" : s === "new" ? "text-blue-300" : s === "contacted" ? "text-yellow-300" : "text-white/70"}
              />
            );
          })}
        </section>

        {/* Filters */}
        <section className="mt-7 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex gap-2 flex-wrap items-center">
            <FilterChip testId="filter-all" active={statusFilter === "all"} onClick={() => { setStatusFilter("all"); setPage(0); }}>
              All ({stats.total})
            </FilterChip>
            {ALL_STATUSES.slice(0, 4).map((s) => (
              <FilterChip
                key={s}
                testId={`filter-${s}`}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(s); setPage(0); }}
              >
                {STATUS_META[s].label} ({statusCount(s)})
              </FilterChip>
            ))}
            {isAdmin && (
              <button
                data-testid="tab-trash"
                onClick={() => { setTrashMode(!trashMode); setPage(0); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${
                  trashMode
                    ? "bg-red-500/20 text-red-100 border-red-500/50"
                    : "bg-white/[0.04] text-white/70 border-white/10 hover:border-red-400/40"
                }`}
              >
                <Trash2 size={12} /> {trashMode ? "Viewing trash" : "Trash"}
              </button>
            )}
          </div>

          {/* Saved Views bar */}
          <div className="flex items-center gap-2 flex-wrap ml-auto" data-testid="saved-views-bar">
            {savedViews.map((v) => (
              <button
                key={v.id}
                data-testid={`saved-view-${v.id}`}
                onClick={() => applyView(v)}
                className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] text-[#F0D788] hover:bg-[#D4AF37]/15"
                title={JSON.stringify(v.filters)}
              >
                {v.name}
                <span
                  onClick={(e) => deleteView(v.id, e)}
                  className="opacity-40 hover:opacity-100 hover:text-red-300 text-[10px]"
                >✕</span>
              </button>
            ))}
            {showSaveViewInput ? (
              <div className="inline-flex items-center gap-1.5">
                <input
                  data-testid="saved-view-name"
                  autoFocus
                  value={saveViewName}
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveCurrentView(); }}
                  placeholder="View name…"
                  className="px-3 py-1.5 rounded-full text-xs bg-[#0A1F44] border border-[#D4AF37]/40 text-white outline-none w-40"
                />
                <button
                  data-testid="saved-view-save"
                  onClick={saveCurrentView}
                  disabled={!saveViewName.trim()}
                  className="px-3 py-1.5 rounded-full text-[11px] btn-gold font-semibold disabled:opacity-50"
                >Save</button>
                <button
                  onClick={() => { setShowSaveViewInput(false); setSaveViewName(""); }}
                  className="px-2 py-1.5 rounded-full text-[11px] text-white/50"
                >Cancel</button>
              </div>
            ) : (
              <button
                data-testid="saved-view-new-btn"
                onClick={() => setShowSaveViewInput(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border border-dashed border-white/20 text-white/60 hover:text-[#F0D788] hover:border-[#D4AF37]/40"
              >
                <Plus size={11} /> Save this view
              </button>
            )}
          </div>
          <div className="relative md:ml-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="admin-search"
              placeholder="Search name / phone / email / interest"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2.5 w-full md:w-80 rounded-full bg-white/[0.04] border border-white/10 text-sm outline-none focus:border-[#D4AF37]/60"
            />
          </div>
        </section>

        {error && (
          <div
            data-testid="admin-error"
            className="mt-6 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
          >
            {String(error)}
          </div>
        )}

        {/* Leads table */}
        <section className="mt-6 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-leads-table" className="w-full text-sm">
              <thead className="bg-white/[0.04] text-white/70 uppercase tracking-wider text-[10px]">
                <tr>
                  {(isAdmin || role === "manager") && (
                    <th className="text-left p-4 w-10">
                      <input
                        data-testid="bulk-select-all"
                        type="checkbox"
                        checked={filteredLeads.length > 0 && filteredLeads.every((l) => selectedIds.includes(l.id))}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedIds(filteredLeads.map((l) => l.id));
                          else setSelectedIds([]);
                        }}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                    </th>
                  )}
                  <th className="text-left p-4">Lead</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Interest · Source</th>
                  <th className="text-left p-4">Message</th>
                  <th className="text-left p-4">Created</th>
                  <th className="text-left p-4">Status · Verification</th>
                  <th className="text-right p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="p-10 text-center text-white/70">Loading leads…</td></tr>
                )}
                {!loading && filteredLeads.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-white/70">{trashMode ? "Trash is empty." : "No leads in this view."}</td></tr>
                )}
                {!loading &&
                  filteredLeads.map((lead) => {
                    const StatusIcon = STATUS_META[lead.status]?.icon || Inbox;
                    return (
                      <tr
                        key={lead.id}
                        data-testid={`admin-lead-row-${lead.id}`}
                        className={`border-t border-white/5 hover:bg-white/[0.03] ${selectedIds.includes(lead.id) ? "bg-[#D4AF37]/[0.04]" : ""}`}
                      >
                        {(isAdmin || role === "manager") && (
                          <td className="p-4 align-top">
                            <input
                              data-testid={`bulk-select-${lead.id}`}
                              type="checkbox"
                              checked={selectedIds.includes(lead.id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedIds([...selectedIds, lead.id]);
                                else setSelectedIds(selectedIds.filter((x) => x !== lead.id));
                              }}
                              className="w-4 h-4 accent-[#D4AF37]"
                            />
                          </td>
                        )}
                        <td className="p-4 align-top">
                          <div className="font-display text-base text-white">{lead.name}</div>
                          <div className="text-[11px] text-white/40 mt-1">ID · {lead.id.slice(0, 8)}</div>
                        </td>
                        <td className="p-4 align-top">
                          <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-white/85 hover:text-[#F0D788]">
                            <Phone size={12} className="text-[#D4AF37]" /> {lead.phone}
                          </a>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-white/65 text-xs mt-1 hover:text-[#F0D788]">
                              <Mail size={11} className="text-[#D4AF37]" /> {lead.email}
                            </a>
                          )}
                          <a
                            href={`https://wa.me/91${lead.phone.replace(/\D/g, "").slice(-10)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 text-emerald-300/85 text-xs mt-1 hover:text-emerald-200"
                          >
                            <MessageCircle size={11} /> WhatsApp
                          </a>
                        </td>
                        <td className="p-4 align-top">
                          <div className="text-white/85">{lead.interest}</div>
                          {lead.source && <div className="text-[11px] text-white/40 mt-1">{lead.source}</div>}
                        </td>
                        <td className="p-4 align-top text-white/65 max-w-xs">
                          {lead.message ? (
                            <span className="line-clamp-3">{lead.message}</span>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                        <td className="p-4 align-top text-white/60 whitespace-nowrap">
                          {new Date(lead.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td className="p-4 align-top">
                          <div className="flex flex-col gap-2">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider w-fit ${STATUS_META[lead.status]?.color || ""}`}
                            >
                              <StatusIcon size={11} />
                              {STATUS_META[lead.status]?.label || lead.status}
                            </span>
                            {(() => {
                              const v = lead.verification_status || "pending";
                              const VM = VERIF_META[v] || VERIF_META.pending;
                              const VIcon = VM.icon;
                              return (
                                <span
                                  data-testid={`verif-badge-${lead.id}`}
                                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider w-fit ${VM.color}`}
                                >
                                  <VIcon size={10} /> {VM.label}
                                </span>
                              );
                            })()}
                            {!trashMode && canWrite && (
                              <select
                                data-testid={`admin-status-select-${lead.id}`}
                                value={lead.status}
                                onChange={(e) => updateStatus(lead, e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-md bg-[#0A1F44] border border-white/10 text-white outline-none focus:border-[#D4AF37]"
                              >
                                {ALL_STATUSES.map((s) => (
                                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </td>
                        <td className="p-4 align-top">
                          <div className="flex flex-col items-end gap-1.5">
                            {!trashMode && canVerify && (lead.verification_status || "pending") !== "verified" && (
                              <button
                                data-testid={`verify-btn-${lead.id}`}
                                onClick={() => setVerifModal({ lead, status: "verified" })}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-[11px] hover:bg-emerald-500/20"
                              >
                                <ShieldCheck size={11} /> Verify
                              </button>
                            )}
                            {!trashMode && canVerify && (lead.verification_status || "pending") !== "rejected" && (
                              <button
                                data-testid={`reject-btn-${lead.id}`}
                                onClick={() => setVerifModal({ lead, status: "rejected" })}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-400/30 text-red-200 text-[11px] hover:bg-red-500/20"
                              >
                                <XCircle size={11} /> Reject
                              </button>
                            )}
                            {!trashMode && isAdmin && lead.status === "closed" && (
                              <button
                                data-testid={`soft-delete-btn-${lead.id}`}
                                onClick={() => setDeleteModal({ lead, kind: "soft" })}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.05] border border-white/15 text-white/85 text-[11px] hover:bg-red-500/10 hover:border-red-400/40 hover:text-red-200"
                              >
                                <Trash2 size={11} /> Soft delete
                              </button>
                            )}
                            {trashMode && isAdmin && (
                              <>
                                <button
                                  data-testid={`restore-btn-${lead.id}`}
                                  onClick={async () => { await AdminLeadOps.restore(lead.id); load(); }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-[11px] hover:bg-emerald-500/20"
                                >
                                  <Undo2 size={11} /> Restore
                                </button>
                                <button
                                  data-testid={`perm-delete-btn-${lead.id}`}
                                  onClick={() => setDeleteModal({ lead, kind: "permanent" })}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-400/30 text-red-200 text-[11px] hover:bg-red-500/20"
                                >
                                  <Trash2 size={11} /> Permanent
                                </button>
                              </>
                            )}
                            {(isAdmin || role === "manager" || role === "receptionist" || role === "operator") && (
                              <button
                                data-testid={`audit-btn-${lead.id}`}
                                onClick={() => setDetailModal({ lead })}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white/60 hover:text-[#F0D788] text-[10px]"
                                title="Timeline + follow-ups + audit"
                              >
                                <History size={10} /> Timeline
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pagination */}
        <section className="mt-6 flex items-center justify-between text-sm">
          <div className="text-white/55">
            Page <span className="text-white">{page + 1}</span> of {pageCount} · {total} total
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-prev-page"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-xs disabled:opacity-40"
            >
              <ChevronLeft size={13} /> Previous
            </button>
            <button
              data-testid="admin-next-page"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-xs disabled:opacity-40"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </section>
      </main>

      {verifModal && (
        <VerificationModal
          {...verifModal}
          onClose={() => setVerifModal(null)}
          onDone={() => { setVerifModal(null); load(); }}
        />
      )}
      {deleteModal && (
        <DeleteLeadModal
          {...deleteModal}
          onClose={() => setDeleteModal(null)}
          onDone={() => { setDeleteModal(null); load(); }}
        />
      )}
      {detailModal && (
        <LeadDetailModal
          lead={detailModal.lead}
          role={role}
          onClose={() => setDetailModal(null)}
          onDone={() => { setDetailModal(null); load(); }}
        />
      )}
      {bulkModal && (
        <BulkActionModal
          {...bulkModal}
          selectedIds={selectedIds}
          onClose={() => setBulkModal(null)}
          onDone={() => { setBulkModal(null); setSelectedIds([]); load(); }}
        />
      )}

      {/* ---------- Floating bulk-action bar ---------- */}
      {selectedIds.length > 0 && (isAdmin || role === "manager") && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 rounded-full bg-[#0A1F44] border border-[#D4AF37]/40 shadow-2xl shadow-black/50 px-5 py-3 flex items-center gap-3"
        >
          <span className="text-sm text-white/85 pr-2">
            <span className="text-[#F0D788] font-semibold">{selectedIds.length}</span> selected
          </span>
          <div className="w-px h-6 bg-white/15" />
          <button
            data-testid="bulk-assign-btn"
            onClick={() => setBulkModal({ action: "assign" })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 hover:border-[#D4AF37]/50 text-xs text-white/90"
          >
            <UserPlus size={12} /> Assign
          </button>
          <button
            data-testid="bulk-status-btn"
            onClick={() => setBulkModal({ action: "status" })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 hover:border-[#D4AF37]/50 text-xs text-white/90"
          >
            <Layers size={12} /> Change status
          </button>
          <button
            data-testid="bulk-verify-btn"
            onClick={() => setBulkModal({ action: "verify" })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 hover:border-emerald-400/60 text-xs text-emerald-200"
          >
            <ShieldCheck size={12} /> Verify
          </button>
          {isAdmin && !trashMode && (
            <button
              data-testid="bulk-soft-delete-btn"
              onClick={() => setBulkModal({ action: "soft_delete" })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-400/30 hover:border-red-400/60 text-xs text-red-200"
            >
              <Trash2 size={12} /> Trash
            </button>
          )}
          {isAdmin && trashMode && (
            <button
              data-testid="bulk-restore-btn"
              onClick={() => setBulkModal({ action: "restore" })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 hover:border-emerald-400/60 text-xs text-emerald-200"
            >
              <Undo2 size={12} /> Restore
            </button>
          )}
          <div className="w-px h-6 bg-white/15" />
          <button
            data-testid="bulk-clear-btn"
            onClick={() => setSelectedIds([])}
            className="p-1.5 rounded-full text-white/60 hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Verification / Delete / Audit modals
// ------------------------------------------------------------
function VerificationModal({ lead, status, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isReject = status === "rejected";
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await AdminLeadOps.verify(lead.id, status, notes.trim() || null);
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }
  return (
    <ModalShell testId="verif-modal" title={`${isReject ? "Reject" : "Verify"} — ${lead.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-white/70">
          {isReject
            ? "Explain briefly why this lead is being rejected — the note is stored in the audit log and shown to any staff who opens the lead."
            : "Add an optional note (KYC observations, confirmation details, exceptions granted, etc.)."}
        </p>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5">Notes {isReject && <span className="text-red-300">(required)</span>}</div>
          <textarea
            data-testid="verif-notes"
            required={isReject}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/15 focus:border-[#D4AF37]/60 outline-none text-sm text-white resize-none"
            placeholder={isReject ? "e.g. Aadhaar and PAN name mismatch." : "e.g. All documents received and verified."}
          />
        </label>
        {err && <div className="text-xs text-red-200">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">Cancel</button>
          <button
            type="submit"
            data-testid="verif-submit"
            disabled={busy || (isReject && !notes.trim())}
            className={`px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60 ${
              isReject ? "bg-red-500 hover:bg-red-600 text-white" : "btn-gold"
            }`}
          >
            {busy ? "Working…" : isReject ? "Reject lead" : "Mark verified"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteLeadModal({ lead, kind, onClose, onDone }) {
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const requiredText = `DELETE ${lead.name}`;
  const isPerm = kind === "permanent";

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isPerm) {
        await AdminLeadOps.permanentDelete(lead.id, confirmation);
      } else {
        await AdminLeadOps.softDelete(lead.id);
      }
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalShell testId={`delete-modal-${kind}`} title={isPerm ? "Permanent delete" : "Move to trash"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-white/75 leading-relaxed">
          {isPerm ? (
            <>
              This permanently erases <span className="text-white">{lead.name}</span>&apos;s lead
              and every attached record. The action is <strong className="text-red-300">not
              reversible</strong>. An audit entry is written before deletion.
            </>
          ) : (
            <>
              Move <span className="text-white">{lead.name}</span> to the Trash. It will no longer
              appear in normal listings, but Admins can restore it from the Trash tab.
            </>
          )}
        </p>
        {isPerm && (
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
              Type <span className="text-red-300 font-mono">{requiredText}</span> to confirm
            </div>
            <input
              data-testid="delete-confirm-input"
              required
              autoFocus
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-red-500/30 focus:border-red-400 outline-none text-sm text-white font-mono"
              placeholder={requiredText}
            />
          </label>
        )}
        {err && <div className="text-xs text-red-200">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">Cancel</button>
          <button
            type="submit"
            data-testid={`delete-confirm-submit-${kind}`}
            disabled={busy || (isPerm && confirmation !== requiredText)}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60 ${
              isPerm ? "bg-red-500 hover:bg-red-600 text-white" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            {busy ? "Working…" : <><Trash2 size={12} /> {isPerm ? "Delete permanently" : "Move to trash"}</>}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function LeadDetailModal({ lead, role, onClose, onDone }) {
  const [tab, setTab] = useState("timeline");
  return (
    <ModalShell testId="lead-detail-modal" title={lead.name} onClose={onClose} wide>
      <div className="text-[11px] text-white/55 -mt-4 mb-4">
        {lead.phone} {lead.email ? ` · ${lead.email}` : ""} · {lead.interest}
      </div>
      <div className="flex gap-1 border-b border-white/10 mb-4">
        {[
          { id: "timeline", label: "Timeline", icon: Activity },
          { id: "followups", label: "Follow-ups", icon: Calendar },
          { id: "audit", label: "Audit", icon: History },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`detail-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-3.5 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 -mb-px transition ${
              tab === id ? "text-[#F0D788] border-[#D4AF37]" : "text-white/55 border-transparent hover:text-white"
            }`}
          >
            <Icon size={11} /> {label}
          </button>
        ))}
      </div>
      {tab === "timeline" && <TimelineTab lead={lead} onChanged={onDone} />}
      {tab === "followups" && <FollowUpsTab lead={lead} role={role} onChanged={onDone} />}
      {tab === "audit" && <AuditTab lead={lead} />}
    </ModalShell>
  );
}

function TimelineTab({ lead, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("note");
  const [body, setBody] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    CRM.timeline(lead.id).then((d) => setRows(d.items || []));
  }, [lead.id]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await CRM.addActivity(lead.id, kind, body.trim());
      setBody("");
      const d = await CRM.timeline(lead.id);
      setRows(d.items || []);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="rounded-2xl border border-[#D4AF37]/25 bg-black/25 p-3">
        <div className="flex gap-2 items-center">
          <select
            data-testid="timeline-add-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/10 text-white/90 text-xs outline-none focus:border-[#D4AF37]"
          >
            <option value="note">Note</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="meeting">Meeting</option>
          </select>
          <input
            data-testid="timeline-add-body"
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Log what happened…"
            className="flex-1 px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/10 text-white text-sm outline-none focus:border-[#D4AF37]"
          />
          <button
            type="submit"
            data-testid="timeline-add-submit"
            disabled={busy || !body.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full btn-gold text-xs font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Log
          </button>
        </div>
        {err && <div className="mt-2 text-[11px] text-red-200">{err}</div>}
      </form>

      <div data-testid="timeline-list" className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
        {!rows && <div className="text-white/60 text-sm py-6 text-center">Loading…</div>}
        {rows && rows.length === 0 && <div className="text-white/60 text-sm py-6 text-center">No history yet.</div>}
        {rows && rows.map((e, i) => (
          <div key={`${e.source}-${e.id}-${i}`} className="rounded-xl bg-black/20 border border-white/[0.06] px-3.5 py-2.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1.5">
                <SourcePill source={e.source} />
                <span className="text-[#F0D788] font-mono">{e.kind}</span>
              </span>
              <span className="text-white/45">{formatTime(e.at)}</span>
            </div>
            {e.body && <div className="mt-1.5 text-sm text-white/90 whitespace-pre-wrap">{e.body}</div>}
            {e.due_at && (
              <div className="mt-1 text-[11px] text-amber-200">
                Due {new Date(e.due_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                {e.status && <> · <span className="text-white/70">{e.status}</span></>}
              </div>
            )}
            {e.actor_email && (
              <div className="mt-1 text-[11px] text-white/45">
                by {e.actor_email}{e.actor_role ? ` · ${e.actor_role}` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FollowUpsTab({ lead, role, onChanged }) {
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("call");
  const [dueAt, setDueAt] = useState(() => {
    const t = new Date(Date.now() + 24 * 3600 * 1000);
    return t.toISOString().slice(0, 16);
  });
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(() => CRM.listFollowUps({ scope: "all" }).then((d) => {
    setRows((d.items || []).filter((f) => f.lead_id === lead.id));
  }), [lead.id]);
  useEffect(() => { load(); }, [load]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await CRM.createFollowUp(lead.id, {
        kind,
        due_at: new Date(dueAt).toISOString(),
        notes: notes.trim() || null,
      });
      setShowAdd(false);
      setNotes("");
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function toggleStatus(f, status) {
    await CRM.updateFollowUp(lead.id, f.id, { status });
    await load();
    onChanged?.();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-white/75 text-sm">
          {rows ? `${rows.length} follow-up${rows.length === 1 ? "" : "s"}` : "Loading…"}
        </div>
        <button
          data-testid="followup-add-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full btn-gold text-xs font-semibold"
        >
          <Plus size={12} /> Schedule
        </button>
      </div>
      {showAdd && (
        <form onSubmit={submit} className="rounded-2xl border border-[#D4AF37]/25 bg-black/25 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Kind</div>
              <select data-testid="followup-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/10 text-white text-sm">
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="task">Task</option>
                <option value="meeting">Meeting</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Due at</div>
              <input data-testid="followup-due-at" required type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/10 text-white text-sm" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Notes (optional)</div>
            <textarea data-testid="followup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/10 text-white text-sm resize-none" placeholder="Reconfirm salary slips…" />
          </label>
          {err && <div className="text-xs text-red-200">{err}</div>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-1.5 rounded-full border border-white/15 text-xs text-white/70">Cancel</button>
            <button type="submit" data-testid="followup-submit" disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full btn-gold text-xs font-semibold disabled:opacity-60">
              {busy ? "Working…" : "Schedule"}
            </button>
          </div>
        </form>
      )}
      <div data-testid="followup-list" className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
        {rows && rows.length === 0 && <div className="text-white/60 text-sm py-6 text-center">No follow-ups scheduled.</div>}
        {(rows || []).map((f) => {
          const overdue = f.status === "open" && new Date(f.due_at) < new Date();
          return (
            <div key={f.id} className={`rounded-xl border px-3.5 py-2.5 ${
              f.status === "done" ? "bg-emerald-500/[0.06] border-emerald-500/25" :
              f.status === "cancelled" ? "bg-white/[0.03] border-white/10" :
              overdue ? "bg-red-500/[0.07] border-red-500/30" :
              "bg-white/[0.03] border-white/10"
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-white/60">{f.kind}</span>
                  <span className={`ml-2 text-xs ${overdue ? "text-red-200" : "text-[#F0D788]"}`}>
                    {new Date(f.due_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                  {f.status === "done" && <span className="ml-2 text-[10px] text-emerald-300">✓ done</span>}
                  {f.status === "cancelled" && <span className="ml-2 text-[10px] text-white/45">cancelled</span>}
                </div>
                {f.status === "open" && (
                  <div className="flex gap-1">
                    <button
                      data-testid={`followup-done-${f.id}`}
                      onClick={() => toggleStatus(f, "done")}
                      className="px-2.5 py-1 rounded-full text-[10px] bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25"
                    >
                      Mark done
                    </button>
                    <button
                      data-testid={`followup-cancel-${f.id}`}
                      onClick={() => toggleStatus(f, "cancelled")}
                      className="px-2.5 py-1 rounded-full text-[10px] text-white/60 hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {f.notes && <div className="mt-1 text-sm text-white/85">{f.notes}</div>}
              <div className="mt-1 text-[10px] text-white/45">
                by {f.created_by} · assigned to {f.assigned_to_email}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AuditTab({ lead }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    AdminLeadOps.audit(lead.id).then((d) => setRows(d.items || []));
  }, [lead.id]);
  if (!rows) return <div className="text-white/60 text-sm py-6 text-center">Loading…</div>;
  if (rows.length === 0) return <div className="text-white/60 text-sm py-6 text-center">No audit entries yet.</div>;
  return (
    <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
      {rows.map((r) => (
        <div key={r._id} className="rounded-xl bg-black/25 border border-white/10 px-4 py-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#F0D788] font-mono">{r.action}</span>
            <span className="text-white/45">{new Date(r.at).toLocaleString()}</span>
          </div>
          <div className="text-[11px] text-white/60 mt-1">
            by <span className="text-white/85">{r.actor_email}</span>
            {r.actor_role && <> · <span className="text-white/55">{r.actor_role}</span></>}
            {r.ip && <> · <span className="text-white/40">{r.ip}</span></>}
          </div>
          {r.meta && Object.keys(r.meta).length > 0 && (
            <div className="mt-2 text-[11px] text-white/55 font-mono break-all">
              {Object.entries(r.meta).map(([k, v]) => (
                <div key={k}>{k}: {String(v)}</div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BulkActionModal({ action, selectedIds, onClose, onDone }) {
  const [assignTo, setAssignTo] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [status, setStatus] = useState("contacted");
  const [verification, setVerification] = useState("verified");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);
  const failedResults = useMemo(
    () => Object.entries(summary?.results || {}).filter(([, r]) => !r.ok),
    [summary],
  );

  useEffect(() => {
    if (action === "assign") {
      AdminStaff.list().then((d) => setStaffList((d.items || []).filter((s) => s.active && s.role !== "admin")));
    }
  }, [action]);

  const title = {
    assign:      "Assign leads",
    status:      "Change status",
    verify:      "Verify / reject leads",
    soft_delete: "Move to Trash",
    restore:     "Restore from Trash",
  }[action];

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(""); setSummary(null);
    try {
      const payload = { action, lead_ids: selectedIds };
      if (action === "assign") payload.assign_to = assignTo || null;
      if (action === "status") payload.status = status;
      if (action === "verify") { payload.verification = verification; payload.verification_notes = notes.trim() || null; }
      const r = await CRM.bulk(payload);
      setSummary(r);
      if (r.failed === 0) {
        setTimeout(onDone, 800);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Bulk action failed.");
    } finally { setBusy(false); }
  }

  return (
    <ModalShell testId={`bulk-modal-${action}`} title={title} onClose={onClose}>
      <p className="text-sm text-white/75 mb-4">
        Applying to <span className="text-[#F0D788] font-semibold">{selectedIds.length}</span> lead(s).
      </p>
      <form onSubmit={submit} className="space-y-3">
        {action === "assign" && (
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">Assignee</div>
            <select data-testid="bulk-assign-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/15 text-white text-sm">
              <option value="">— Unassign —</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>{s.name || s.email} · {s.role}</option>
              ))}
            </select>
          </label>
        )}
        {action === "status" && (
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">New status</div>
            <select data-testid="bulk-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/15 text-white text-sm">
              {ALL_STATUSES.map((s) => (<option key={s} value={s}>{STATUS_META[s].label}</option>))}
            </select>
          </label>
        )}
        {action === "verify" && (
          <>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">Outcome</div>
              <select data-testid="bulk-verify-select" value={verification} onChange={(e) => setVerification(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/15 text-white text-sm">
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">Notes (applied to all)</div>
              <textarea data-testid="bulk-verify-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg bg-[#0A1F44] border border-white/15 text-white text-sm resize-none" />
            </label>
          </>
        )}
        {err && <div className="text-xs text-red-200">{err}</div>}
        {summary && (
          <div
            className={`rounded-lg border px-3 py-2 text-xs ${
              summary.failed > 0
                ? "bg-rose-500/10 border-rose-500/30 text-rose-100"
                : "bg-emerald-500/10 border-emerald-500/30 text-emerald-100"
            }`}
          >
            <div className="font-semibold mb-1">
              {summary.succeeded} succeeded{summary.failed > 0 && `, ${summary.failed} failed`}.
            </div>
            {summary.failed > 0 && summary.results && (
              <ul className="space-y-0.5 text-[11px] text-white/75 max-h-32 overflow-auto">
                {failedResults.map(([id, r]) => (
                    <li key={id}>
                      <span className="font-mono text-white/50">{id.slice(0, 8)}</span> —{" "}
                      <span className="text-rose-200">{r.error || "failed"}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">Close</button>
          <button
            type="submit"
            data-testid="bulk-submit"
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60 ${
              action === "soft_delete" ? "bg-red-500 hover:bg-red-600 text-white" : "btn-gold"
            }`}
          >
            {busy ? "Working…" : "Apply"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function SourcePill({ source }) {
  const map = {
    activity: "bg-blue-500/15 text-blue-200 border-blue-400/25",
    audit:    "bg-white/10 text-white/70 border-white/15",
    followup: "bg-amber-500/15 text-amber-200 border-amber-400/30",
    system:   "bg-[#D4AF37]/15 text-[#F0D788] border-[#D4AF37]/40",
  }[source] || "bg-white/10 text-white/70 border-white/10";
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border ${map}`}>{source}</span>;
}

function formatTime(iso) {
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function Loader2({ size = 12, className = "" }) {
  return <span className={className} style={{ display: "inline-block", width: size, height: size, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />;
}

function ModalShell({ title, testId, onClose, wide = false, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid={testId} className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-3xl bg-[#0A1F44] border border-[#D4AF37]/30 shadow-2xl`}>
        <div className="flex items-center justify-between px-6 pt-6">
          <div className="font-display text-lg text-white">{title}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function StatCard({ testId, label, value, accent = "text-[#F0D788]" }) {
  return (
    <div
      data-testid={testId}
      className="group relative rounded-2xl p-5 border border-[#D4AF37]/25 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent hover:border-[#D4AF37]/60 hover:-translate-y-0.5 transition-all overflow-hidden"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#D4AF37]/10 blur-2xl opacity-0 group-hover:opacity-100 transition" />
      <div className="text-[10px] tracking-wider uppercase text-white/55 leading-none mb-2 relative">
        {label}
      </div>
      <div className={`font-display text-4xl leading-none relative ${accent}`}>{value}</div>
    </div>
  );
}

function FilterChip({ children, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs border transition ${
        active
          ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37]"
          : "bg-white/[0.04] text-white/75 border-white/10 hover:border-[#D4AF37]/40"
      }`}
    >
      {children}
    </button>
  );
}
